import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { BodyTableCell } from '../shared';
import { toCapitalized, currencyFormatter } from 'core/helpers/formatters';
import { Label } from 'core';
import { TruncatedText } from 'core/components/truncated-text';
import { convertUTCToET } from 'customer/components/policy/dates';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import RevertVersionModal from '../revert-version-modal';
import VersionActions from './version-actions';
import { versionChangeList } from '../format-changes';
import styles, { useStyles } from '../versions.styles';

const TableCell = withStyles(styles)(({ children, classes }) => (
  <BodyTableCell classes={{ tableCell: classes.bigTableCell }}>{children}</BodyTableCell>
));

TableCell.propTypes = {
  children: PropTypes.node.isRequired
};

export const ListBody = ({
  itemsChanged,
  coverageChange,
  version,
  updatedDateTime,
  username,
  internalNotes,
  changeInPremium,
  classes,
  showTotalPriceColumn,
  totalPrice,
  currentVersion,
  policyType,
  closeVersionHistoryModal
}) => {
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [versionToRevertTo, setVersionToRevertTo] = useState(currentVersion);
  const { canRevertPolicyDetails, canViewClarionDoorData } = useSession();
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  // convertUTCToET returns "MM/dd/yyyy @ h:mm a" — split so the row shows the day in the larger
  // text and the time in the smaller text (no duplicated date). Both stay in ET. See GH #29604.
  const [datePartET, timePartET] = convertUTCToET(updatedDateTime).split(' @ ');
  const style = useStyles();

  // Compare-to-previous opens the ClarionDoor diff between this version and the prior one. Only
  // available from version 002 onward, and not for farm policies (which use a per-risk ClarionDoor
  // map the version snapshot doesn't expose). See GH #29604.
  const policyId = policyStore.policy?.id;
  const accountId = policyId?.split('-')[0];
  const isFarm = isFarmBasedPolicyType(policyType);
  const canCompareToPrevious = version > 1 && !isFarm;
  const compareDisabledTooltip = isFarm
    ? 'Comparison is not supported for farm policies'
    : 'No prior version to compare to';
  const canRevert = currentVersion !== version;
  // The actions cell (Compare + Revert icons) shows if the operator can use either action.
  const showActions = canViewClarionDoorData || canRevertPolicyDetails;

  return (
    <>
      {canRevertPolicyDetails && (
        <RevertVersionModal
          open={openRevertModal}
          onClose={() => setOpenRevertModal(false)}
          versionToRevertTo={versionToRevertTo}
          policyType={policyType}
          closeVersionHistoryModal={closeVersionHistoryModal}
        />
      )}
      <TableRow>
        <TableCell>{`00${version}`.substr(-3)}</TableCell>
        <TableCell>
          <span className={classes.dateInfo}>{datePartET}</span>
          <Label type="infoLabel">{timePartET} ET</Label>
        </TableCell>
        <TableCell>{username !== 'initial-purchase' ? toCapitalized(username) : 'Initial Purchase'}</TableCell>
        <TableCell>
          <TruncatedText items={versionChangeList(coverageChange)} text="None" />
        </TableCell>
        <TableCell>
          <TruncatedText items={versionChangeList(itemsChanged)} text="None" />
        </TableCell>
        <TableCell>
          <TruncatedText text={internalNotes || ''} className={classes.internalNotes} />
        </TableCell>
        <TableCell>{currencyFormatter(changeInPremium)}</TableCell>
        {showTotalPriceColumn && (
          <TableCell>
            <span className={!totalPrice ? style.emptyState : null}>
              {totalPrice ? currencyFormatter(totalPrice) : `View this version's dec page for total price`}
            </span>
          </TableCell>
        )}
        {showActions && (
          <TableCell>
            <VersionActions
              showCompare={canViewClarionDoorData}
              compareHref={`/customer/${accountId}/policy/${policyId}/version-compare/${version}`}
              canCompare={canCompareToPrevious}
              compareDisabledTooltip={compareDisabledTooltip}
              showRevert={canRevertPolicyDetails}
              canRevert={canRevert}
              onRevert={() => {
                setVersionToRevertTo(version);
                setOpenRevertModal(true);
              }}
            />
          </TableCell>
        )}
      </TableRow>
    </>
  );
};

ListBody.propTypes = {
  itemsChanged: PropTypes.string.isRequired,
  coverageChange: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired,
  updatedDateTime: PropTypes.string.isRequired,
  changeInPremium: PropTypes.number,
  username: PropTypes.string.isRequired,
  internalNotes: PropTypes.string,
  state: PropTypes.string,
  classes: PropTypes.object.isRequired,
  showTotalPriceColumn: PropTypes.bool,
  totalPrice: PropTypes.number,
  currentVersion: PropTypes.number,
  policyType: PropTypes.string.isRequired,
  closeVersionHistoryModal: PropTypes.func.isRequired
};

ListBody.defaultProps = {
  internalNotes: undefined,
  changeInPremium: 0.0,
  state: undefined,
  showTotalPriceColumn: false,
  totalPrice: undefined
};

export default withStyles(styles)(ListBody);
